import { Link } from "react-router-dom";
import type { SearchResultItem } from "../../api/types";
import { TypeChip } from "../TypeChip";
import { VisibilityBadge } from "../VisibilityBadge";
import { accession, formatDate, formatYear, TYPE_PREFIX_LABEL } from "../../lib/format";

/**
 * One uniform result row, used identically by the blog feed and the site-wide
 * search page. Every content type renders the same shape — thumbnail, type
 * chip, accession number, title, summary, date, tags — so the results list
 * reads like a single coherent catalogue.
 *
 * Every result links internally:
 *   - Journal entries → /blog/<slug> (provided as item.url)
 *   - Everything else → /documents/<content_id> (item.id is the content id),
 *     where the item is shown in full alongside its comment thread.
 */
interface ResultCardProps {
  item: SearchResultItem;
}

function destination(item: SearchResultItem): string {
  return item.type === "BLOG_POST" ? item.url : `/documents/${item.id}`;
}

export function ResultCard({ item }: ResultCardProps) {
  const to = destination(item);

  const inner = (
    <>
      <div className={`result__thumb${item.thumbnailUrl ? "" : " result__thumb--empty"}`}>
        {item.thumbnailUrl ? (
          <img src={item.thumbnailUrl} alt="" loading="lazy" />
        ) : (
          <span>{TYPE_PREFIX_LABEL[item.type]}</span>
        )}
      </div>

      <div className="result__body">
        <div className="result__top">
          <TypeChip type={item.type} />
          <VisibilityBadge visibility={item.visibility} />
          <span className="accession">{accession(item.type, item.sourceId)}</span>
        </div>

        <h3 className="result__title">{item.title}</h3>

        {item.summary && <p className="result__summary">{item.summary}</p>}

        <div className="result__foot">
          {item.occurredAt && (
            <span className="result__date">
              {item.type === "PHOTO"
                ? formatYear(item.occurredAt)
                : formatDate(item.occurredAt)}
            </span>
          )}
          {item.tags.slice(0, 4).map((tag) => (
            <span key={tag} className="tag">
              {tag}
            </span>
          ))}
        </div>
      </div>
    </>
  );

  return (
    <Link className="result" to={to}>
      {inner}
    </Link>
  );
}
